/**
 * Find the YouTube highlight/résumé video that matches a given match
 * by inspecting the video titles.
 *
 * Heuristics (in order):
 *  1. Title contains opponent name AND looks like a résumé (`résumé`, `highlights`, `vs`, score pattern `xx-xx`)
 *  2. Title contains opponent name AND was published within 5 days of the match date
 *  3. Title contains opponent name (loose fallback)
 */
const NORM = (s) => (s || "")
  .toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // strip accents
  .replace(/[^a-z0-9 ]/g, " ")
  .replace(/\s+/g, " ")
  .trim();

const RESUME_TOKENS = ["resume", "highlights", "highlight", "best of", "compte rendu", "le match", "vs", "match"];

function _opponentTokens(match, ourCode) {
  // Take the team that ISN'T us
  const homeIsUs = (match.home_code || "").toUpperCase() === (ourCode || "").toUpperCase();
  const opp = homeIsUs ? match.away_team : match.home_team;
  if (!opp) return [];
  const norm = NORM(opp);
  return norm.split(" ").filter((w) => w.length >= 4); // skip noise tokens
}

export function findResumeVideo(match, videos, ourCode) {
  if (!match || !videos || videos.length === 0) return null;
  const oppTokens = _opponentTokens(match, ourCode);
  if (oppTokens.length === 0) return null;
  const matchDate = match.date_iso ? new Date(match.date_iso) : null;

  const scored = videos.map((v) => {
    const title = NORM(v.title || "");
    let score = 0;
    let hasOpp = false;
    for (const t of oppTokens) if (title.includes(t)) { score += 5; hasOpp = true; }
    if (!hasOpp) return null;
    for (const r of RESUME_TOKENS) if (title.includes(r)) { score += 2; break; }
    // Score pattern e.g. "27-31" or "27 31"
    if (/\d{2}\s*[-_·\s]\s*\d{2}/.test(title)) score += 3;
    if (matchDate && v.published_at) {
      const dv = new Date(v.published_at);
      const days = Math.abs((dv - matchDate) / 86400000);
      if (days <= 7) score += 4;
      else if (days <= 30) score += 1;
    }
    return { v, score };
  }).filter(Boolean);

  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.v || null;
}

/**
 * Returns the best public URL for the video.
 */
export function videoWatchUrl(v) {
  if (!v) return null;
  const id = v.id || v.video_id;
  if (!id) return v.url || null;
  return `https://www.youtube.com/watch?v=${id}`;
}
